import piexif from 'piexifjs'

/**
 * 画像を EXIF メタデータ付きでダウンロードする
 *
 * JPEG 画像の場合は EXIF DateTimeOriginal を設定してダウンロード。
 * 非 JPEG 画像の場合は EXIF 操作をスキップして直接ダウンロード。
 */
export async function downloadImageWithExif(
  blobUrl: string,
  metadata: {
    date: string
    memberName: string
    key: string
  }
): Promise<void> {
  try {
    // 1. Blob URL から blob を取得
    const response = await fetch(blobUrl)
    const blob = await response.blob()

    // 2. JPEG かどうかを確認
    const isJpeg = blob.type === 'image/jpeg' || blob.type === 'image/jpg'

    if (!isJpeg) {
      // JPEG でない場合は EXIF 操作をスキップして直接ダウンロード
      const filename = generateFilename(metadata)
      downloadBlobDirectly(blob, filename)
      return
    }

    // 3. Blob を ArrayBuffer に変換
    const arrayBuffer = await blob.arrayBuffer()
    const binaryString = arrayBufferToBinaryString(arrayBuffer)

    // 4. 既存の EXIF データを読み込み（なければ空を作成）
    let exifObj: any
    try {
      exifObj = piexif.load(binaryString)
    } catch (error) {
      exifObj = { '0th': {}, Exif: {}, GPS: {} }
    }

    // 5. DateTimeOriginal を設定
    const exifDateTime = convertIsoToExifFormat(metadata.date)
    exifObj.Exif[piexif.ExifIFD.DateTimeOriginal as number] = exifDateTime

    // 6. EXIF データを画像に挿入
    const exifBytes = piexif.dump(exifObj)
    const newBinaryString = piexif.insert(exifBytes, binaryString)

    // 7. 新しい Blob を作成
    const newArrayBuffer = binaryStringToArrayBuffer(newBinaryString)
    const newBlob = new Blob([newArrayBuffer], { type: 'image/jpeg' })

    // 8. ダウンロードをトリガー
    const filename = generateFilename(metadata)
    const downloadUrl = URL.createObjectURL(newBlob)
    const anchor = document.createElement('a')
    anchor.href = downloadUrl
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    console.error('Failed to download image with EXIF:', error)

    // フォールバック: EXIF 操作なしでダウンロード
    try {
      const response = await fetch(blobUrl)
      const blob = await response.blob()
      const filename = generateFilename(metadata)
      downloadBlobDirectly(blob, filename)
    } catch (fallbackError) {
      console.error('Failed to download image (fallback):', fallbackError)
      throw new Error('画像のダウンロードに失敗しました。もう一度お試しください。')
    }
  }
}

/**
 * ArrayBuffer をバイナリ文字列に変換
 */
function arrayBufferToBinaryString(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] as number)
  }
  return binary
}

/**
 * バイナリ文字列を ArrayBuffer に変換
 */
function binaryStringToArrayBuffer(binary: string): ArrayBuffer {
  const buffer = new ArrayBuffer(binary.length)
  const view = new Uint8Array(buffer)
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i)
  }
  return buffer
}

/**
 * ISO 日付文字列を EXIF 形式に変換
 *
 * ISO 形式: "2024-01-15T12:30:45.000Z"
 * EXIF 形式: "2024:01:15 12:30:45"
 */
function convertIsoToExifFormat(isoDate: string): string {
  const date = new Date(isoDate)

  // 無効な日付の場合は現在日時を使用
  if (isNaN(date.getTime())) {
    return formatDateToExif(new Date())
  }

  return formatDateToExif(date)
}

/**
 * Date オブジェクトを EXIF 形式の文字列に変換
 */
function formatDateToExif(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${year}:${month}:${day} ${hours}:${minutes}:${seconds}`
}

/**
 * メタデータから適切なファイル名を生成
 *
 * フォーマット: YYYY-MM-DD_メンバー名.拡張子
 */
function generateFilename(metadata: {
  date: string
  memberName: string
  key: string
}): string {
  const date = new Date(metadata.date)

  // フォーマット: YYYY-MM-DD
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const dateStr = `${year}-${month}-${day}`

  // メンバー名のサニタイズ（無効な文字を削除、日本語は保持）
  const sanitizedName = metadata.memberName
    .replace(/[<>:"/\\|?*]/g, '')
    .trim()

  // key から拡張子を抽出（フォールバックは .jpg）
  const extension =
    metadata.key.match(/\.(jpg|jpeg|png|webp)$/i)?.[0] || '.jpg'

  return `${dateStr}_${sanitizedName}${extension}`
}

/**
 * Blob を直接ダウンロード（EXIF 操作なし）
 */
function downloadBlobDirectly(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}
